import React from 'react';
import { useAuth } from '@/context/AuthContext';
import { useQuery } from '@tanstack/react-query';
import api, { CHURCH } from '@/api/client';
import { Link } from 'react-router-dom';
import { User, Users, HandHeart, ListChecks, Calendar, ArrowRight, Sparkles } from 'lucide-react';

export default function Dashboard() {
  const { user } = useAuth();
  const { data: tasks = [] } = useQuery({ queryKey: ['my-tasks'], queryFn: async () => (await api.get('/member/tasks')).data });
  const { data: rsvps = [] } = useQuery({ queryKey: ['my-rsvps'], queryFn: async () => (await api.get('/member/rsvps')).data });
  const { data: volunteer = [] } = useQuery({ queryKey: ['my-volunteer'], queryFn: async () => (await api.get('/member/volunteer-requests')).data });

  const cards = [
    { to: '/member/profile', icon: User, title: 'My Profile', desc: 'Update personal info, address, family details' },
    { to: '/member/family', icon: Users, title: 'Family Members', desc: 'Add family members and link your household' },
    { to: '/member/volunteer', icon: HandHeart, title: 'Volunteer Teams', desc: 'Join Sunday School, Worship, Media and more' },
    { to: '/member/tasks', icon: ListChecks, title: 'My Tasks', desc: 'View and update tasks assigned to you' },
    { to: '/member/rsvps', icon: Calendar, title: 'My RSVPs', desc: 'See events you\u2019ve responded to' },
  ];

  return (
    <div className="space-y-8">
      <div className="card-moj p-8 gradient-purple text-white">
        <div className="text-moj-gold text-xs uppercase tracking-widest font-bold mb-1">Welcome home</div>
        <h1 className="font-serif text-3xl text-white">Shalom, {user?.first_name || user?.full_name?.split(' ')[0] || 'beloved'} 💛</h1>
        <p className="text-white/90 mt-2 italic">“{CHURCH.motto}” — {CHURCH.scripture}</p>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <div className="card-moj p-5"><ListChecks size={26} className="text-moj-gold-deep mb-2"/><div className="text-3xl font-bold text-moj-purple-deep">{tasks.length}</div><div className="text-sm text-moj-charcoal/70">Active Tasks</div></div>
        <div className="card-moj p-5"><Calendar size={26} className="text-moj-gold-deep mb-2"/><div className="text-3xl font-bold text-moj-purple-deep">{rsvps.length}</div><div className="text-sm text-moj-charcoal/70">RSVPs</div></div>
        <div className="card-moj p-5"><HandHeart size={26} className="text-moj-gold-deep mb-2"/><div className="text-3xl font-bold text-moj-purple-deep">{volunteer.length}</div><div className="text-sm text-moj-charcoal/70">Volunteer Requests</div></div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((c) => (
          <Link key={c.to} to={c.to} className="card-moj p-6 group block" data-testid={`dash-card-${c.to.split('/').pop()}`}>
            <c.icon size={28} className="text-moj-gold-deep mb-3"/>
            <h3 className="font-serif text-lg text-moj-purple-deep mb-1">{c.title}</h3>
            <p className="text-sm text-moj-charcoal/70 mb-3">{c.desc}</p>
            <span className="inline-flex items-center gap-1 text-sm font-semibold text-moj-purple group-hover:gap-2 transition-all">Open <ArrowRight size={16}/></span>
          </Link>
        ))}
      </div>

      {tasks.length > 0 && (
        <div className="card-moj p-6">
          <h3 className="font-serif text-xl text-moj-purple-deep mb-4">Recent Tasks</h3>
          <div className="space-y-3">
            {tasks.slice(0, 3).map((t) => (
              <div key={t.id} className="flex items-center justify-between p-3 rounded-lg bg-moj-cream">
                <div>
                  <div className="font-semibold text-moj-purple-deep">{t.title}</div>
                  <div className="text-xs text-moj-charcoal/60">{t.department} • Due {t.due_date}</div>
                </div>
                <span className="text-xs font-semibold px-3 py-1 rounded-full bg-moj-gold/20 text-moj-purple-deep">{t.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
